/**
 * Build-time configuration for the Vaarta Converse chat assistant.
 *
 * These three values are public by design — the widget URL and the public key
 * are meant to ship in the browser bundle. The *signing secret* is not here and
 * must never be: it lives only in the backend, which mints the identity token
 * at `/api/chatbot-token/`. If a secret ever appears in this file, any visitor
 * can vouch for any user.
 *
 * They arrive through `dotenv-webpack` (see `config/webpack.common.ts`), which
 * inlines `process.env.*` at build time. Two consequences worth knowing before
 * you change anything:
 *
 * 1. These are **frozen into the bundle when webpack runs**. Changing them
 *    later needs a rebuild and redeploy — editing a container env var and
 *    restarting does nothing at all. This is not a theoretical footgun: the
 *    Social app shipped a production bundle carrying the *wrong environment's*
 *    public key, and because the runtime env looked correct, it cost a full
 *    debugging cycle to find.
 *
 * 2. `CONVERSE_PUBLIC_KEY` and the backend's `CHATBOT_SDK_SIGNING_SECRET` must
 *    belong to **the same assistant**. Converse resolves the assistant from the
 *    public key, then checks the token against that assistant's own secret; a
 *    mismatched pair fails with "Invalid host token signature" while both
 *    values look perfectly well-formed.
 *
 * Treat (publicKey, signingSecret, apiBaseUrl) as one inseparable triple.
 */
export const converseConfig = {
    widgetUrl: process.env.CONVERSE_WIDGET_URL || '',
    apiBaseUrl: process.env.CONVERSE_API_BASE_URL || '',
    publicKey: process.env.CONVERSE_PUBLIC_KEY || '',
};
/**
 * Whether the assistant is configured well enough to mount.
 *
 * An unconfigured environment (a local checkout without a `.env`, or a build
 * that predates the variables) must render nothing and throw nothing — a
 * missing assistant is a silent no-op, never a broken page.
 */
export const isConverseConfigured = () => Boolean(converseConfig.widgetUrl &&
    converseConfig.apiBaseUrl &&
    converseConfig.publicKey);
/**
 * Absolute URL of our own endpoint that mints the Converse identity token.
 *
 * Absolute, not relative. Social can write `/api/chatbot-token` because its
 * minting route is a Next.js route on the same origin as the page. Here the
 * frontend is served by nginx and the endpoint lives in the Django backend on
 * a different host, so a relative path would resolve against the frontend and
 * 404 — locally it would hit the webpack dev server on :3002.
 *
 * `REACT_APP_API_BASE_URL` already ends in `/api` (it is the same base every
 * other call in `api-service.ts` uses), so only the view's own path is added.
 */
export const chatbotTokenUrl = () => {
    const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
    return base ? `${base}/chatbot-token/` : '/api/chatbot-token/';
};
/**
 * Pages a CANDIDATE uses: the interview itself (pre-check, the live interview and its end
 * screens all live under `/interview/:slug`) and preferred-slot selection. The recruiter
 * assistant must never appear there — it is a recruiter tool, and it showed up for the whole
 * interview whenever the browser also held a recruiter session.
 *
 * `/interview-report/...` (recruiter) does not match: it has no slash after "interview".
 */
const CANDIDATE_PAGES = [/^\/interview\/[^/]+/, /^\/select-preferred-slots\/[^/]+/];
export const isCandidatePage = (pathname) => CANDIDATE_PAGES.some((pattern) => pattern.test(pathname));
